import { forwardRef } from "react";
import { ActivityIndicator, Pressable, StyleSheet, View } from "react-native";

import { useLayout } from "@smartrent/hooks";

import { useTheme } from "../../utils/theme";
import { Typography } from "../Typography";
import { ButtonAdornment } from "./ButtonAdornment";
import { LongPress } from "./LongPress";
import { useButtonStyles } from "./useButtonStyles";
import { usePressableLink } from "./usePressableLink";
import { useLongPress } from "./useLongPress";

import type { ButtonAdornmentProps } from "./ButtonAdornment";
import type { IconProps } from "@smartrent/icons";
import type {
  AccessibilityProps,
  PressableProps,
  StyleProp,
  TextStyle,
  TouchableWithoutFeedbackProps,
  ViewStyle,
} from "react-native";
import type { ComponentType, ReactNode, Ref } from "react";
import type { TestProps } from "../../types";
import type { ButtonColors, ButtonSize, ButtonVariation } from "./types";

export interface ButtonProps
  extends AccessibilityProps,
    Pick<PressableProps, "onPressIn" | "onPressOut">,
    TestProps {
  /**
   * Allow overriding the inner container <View /> styles
   */
  contentStyle?: StyleProp<ViewStyle>;
  disabled?: boolean;
  /**
   * Allow overriding the parent hover state so child components can be styled based on the hover state
   */
  hovered?: boolean;
  iconLeft?: ComponentType<IconProps>;
  iconLeftProps?: Partial<ButtonAdornmentProps>;
  iconRight?: ComponentType<IconProps>;
  iconRightProps?: Partial<ButtonAdornmentProps>;
  onPress?: TouchableWithoutFeedbackProps["onPress"];
  size?: ButtonSize;
  /**
   * Allow overriding the <Pressable /> styles
   */
  style?: StyleProp<ViewStyle>;
  /**
   * Allow overriding the <Text /> styles
   */
  textStyle?: StyleProp<TextStyle>;
  variation?: ButtonVariation;
  color?: ButtonColors;
  onLongPress?: VoidFunction;
  duration?: number;
  isLoading?: boolean;
  children: ReactNode;

  /**
   * @platform web
   */
  href?: string;
  /**
   * @platform web
   */
  target?: string;
  /**
   * @platform web
   */
  rel?: string;
  /**
   * @platform web
   */
  download?: string;
  to?: string;
}

/**
 * A button component with default styles.
 *
 * example: https://smartrent-ui.com/components/general/button
 */
export const Button = forwardRef(function Button(
  {
    children,
    contentStyle,
    disabled = false,
    isLoading = false,
    iconLeft: IconLeft,
    iconLeftProps,
    iconRight: IconRight,
    iconRightProps,
    onPress: onParentPress,
    size,
    style,
    testID,
    textStyle,
    variation = "default",
    color = "primary",

    //link
    href,
    rel,
    target,
    download,
    to,

    //onPress functions
    onLongPress,
    onPressIn,
    onPressOut,
    duration = 1000,
    ...props
  }: ButtonProps,
  ref: Ref<View>
) {
  const { layout } = useLayout();
  const { colors, config, rounding } = useTheme();

  const pressableLinkProps = usePressableLink({
    onPress: onParentPress,
    to,
    target,
    rel,
    download,
    href,
  });

  const {
    buttonStyles,
    textSizeStyles,
    sizeStyles,
    textColorStyles,
    activityIndicatorStyles,
    iconColorStyles,
  } = useButtonStyles({
    variation,
    color,
    disabled,
    size,
    isLoading,
  });

  const { longPressFinished, pressableProps, longPressProps } = useLongPress({
    dotSize: 2,
    duration,
    layout,
    onLongPress,
  });

  return (
    <Pressable
      accessibilityRole={href || to ? "link" : "button"}
      onPressIn={onPressIn}
      onPressOut={onPressOut}
      style={({ pressed, hovered }) => [
        styles.root,
        buttonStyles.normal,
        pressed || (props.hovered === undefined ? hovered : props.hovered)
          ? buttonStyles?.active
          : undefined,
        {
          borderRadius: rounding.md,
          cursor: isLoading ? "default" : null,
        },
        style,
      ]}
      disabled={disabled}
      testID={testID}
      ref={ref}
      {...props}
      {...pressableProps}
      {...pressableLinkProps}
    >
      {({ pressed, hovered }) => {
        return typeof children === "string" ? (
          <>
            <LongPress {...longPressProps} color={color} />
            <View
              style={[
                styles.content,
                sizeStyles,
                {
                  margin: variation === "outlined" ? -2 : 0, // Artificially remove 2px height/width because we'll have a border on all sides from the Pressable's getButtonStyles()
                },
                contentStyle,
              ]}
            >
              <ButtonAdornment
                Icon={IconLeft}
                type="left"
                isLoading={isLoading}
                pressed={pressed}
                hovered={hovered}
                variation={variation}
                color={color}
                size={size}
                disabled={disabled}
                {...iconLeftProps}
              />

              <Typography
                weight="semibold"
                selectable={false}
                style={[
                  {
                    textTransform: config.capitalizeButtonLabel
                      ? "capitalize"
                      : "none",
                    opacity: isLoading && !IconLeft && !IconRight ? 0 : 1,
                  },
                  textColorStyles.normal,
                  pressed || hovered ? textColorStyles?.active : undefined,
                  longPressFinished ? { color: colors.textTitle } : undefined,
                  textSizeStyles,
                  textStyle,
                ]}
              >
                {children}
              </Typography>
              {isLoading && !IconLeft && !IconRight ? (
                <ActivityIndicator
                  accessibilityLabel="loading"
                  style={activityIndicatorStyles}
                  color={iconColorStyles.normal.color}
                />
              ) : null}
              <ButtonAdornment
                Icon={IconRight}
                type="right"
                isLoading={isLoading}
                pressed={pressed}
                hovered={hovered}
                variation={variation}
                color={color}
                size={size}
                disabled={disabled}
                {...iconRightProps}
              />
            </View>
          </>
        ) : (
          <>
            <LongPress {...longPressProps} color={color} />
            {children}
          </>
        );
      }}
    </Pressable>
  );
});

Button.displayName = "Button";

export const styles = StyleSheet.create({
  root: {
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    overflow: "hidden", // Force the borderRadius crop on Touchable descendents (specifically LinearGradient)
  },
  content: {
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
  },
});
